import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Briefcase, Plus, Camera, UserPlus, UserMinus, Trash2 } from 'lucide-react';
import { canEdit } from '@/lib/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Gear({ user }) {
  const { t } = useTranslation();
  const [kits, setKits] = useState([]);
  const [items, setItems] = useState([]);
  const [photographers, setPhotographers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openKit, setOpenKit] = useState(false);
  const [openItem, setOpenItem] = useState(false);
  const [kitForm, setKitForm] = useState({ name: '', type: 'Tele', items: [], mount_type: '' });
  const [itemForm, setItemForm] = useState({ name: '', type: 'lens', mount: '', focal_range: '' });
  const [assignTarget, setAssignTarget] = useState(null); // { kind: 'kit'|'item', id, name }
  const [assignTo, setAssignTo] = useState('');

  const userCanEdit = canEdit(user, 'gear');
  const photographerById = useMemo(() => {
    const m = {};
    photographers.forEach((p) => { m[p.id] = p; });
    return m;
  }, [photographers]);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [kitsRes, itemsRes, photogsRes] = await Promise.all([
        axios.get(`${API}/gear/kits`),
        axios.get(`${API}/gear/items`),
        axios.get(`${API}/photographers`),
      ]);
      setKits(kitsRes.data);
      setItems(itemsRes.data);
      setPhotographers(photogsRes.data);
    } catch (error) {
      toast.error(t('gear.loadError', { defaultValue: 'Failed to fetch gear' }));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/gear/kits`, kitForm);
      toast.success(t('gear.kitCreated', { defaultValue: 'Kit created!' }));
      setOpenKit(false);
      fetchAll();
      setKitForm({ name: '', type: 'Tele', items: [], mount_type: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('gear.kitCreateError', { defaultValue: 'Failed to create kit' }));
    }
  };

  const handleCreateItem = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/gear/items`, itemForm);
      toast.success(t('gear.itemAdded', { defaultValue: 'Item added!' }));
      setOpenItem(false);
      fetchAll();
      setItemForm({ name: '', type: 'lens', mount: '', focal_range: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('gear.itemAddError', { defaultValue: 'Failed to add item' }));
    }
  };

  const openAssign = (kind, gear) => {
    setAssignTarget({ kind, id: gear.id, name: gear.name, current: gear.owner_photographer_id || '' });
    setAssignTo(gear.owner_photographer_id || '');
  };

  const submitAssign = async () => {
    if (!assignTarget) return;
    if (!assignTo) {
      toast.error(t('gear.pickPhotographer', { defaultValue: 'Pick a photographer' }));
      return;
    }
    const url = `${API}/gear/${assignTarget.kind}s/${assignTarget.id}/assign`;
    try {
      await axios.post(url, { photographer_id: assignTo });
      toast.success(t('gear.assigned', { defaultValue: 'Assigned' }));
      setAssignTarget(null);
      fetchAll();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.error === 'kit_already_assigned' || detail?.error === 'item_already_assigned') {
        toast.error(t('gear.alreadyAssigned', {
          defaultValue: 'Already assigned to {{name}}',
          name: detail.owner_name || '—',
        }));
      } else {
        toast.error(detail || t('gear.assignError', { defaultValue: 'Could not assign' }));
      }
    }
  };

  const unassign = async (kind, gearId) => {
    const url = `${API}/gear/${kind}s/${gearId}/assign`;
    try {
      await axios.delete(url);
      toast.success(t('gear.unassigned', { defaultValue: 'Released' }));
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('gear.assignError', { defaultValue: 'Could not unassign' }));
    }
  };

  const deleteGear = async (kind, gearId, name) => {
    if (!window.confirm(t('gear.deleteConfirm', { defaultValue: 'Delete {{name}}?', name }))) return;
    try {
      await axios.delete(`${API}/gear/${kind}s/${gearId}`);
      toast.success(t('gear.deleted', { defaultValue: 'Deleted' }));
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('gear.deleteError', { defaultValue: 'Could not delete' }));
    }
  };

  const renderOwnerBadge = (gear) => {
    if (!gear.owner_photographer_id) {
      return (
        <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
          {t('gear.available', { defaultValue: 'Available' })}
        </Badge>
      );
    }
    const p = photographerById[gear.owner_photographer_id];
    return (
      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-800 border-amber-200">
        {t('gear.heldBy', { defaultValue: 'Held by' })} {p?.name || '—'}
      </Badge>
    );
  };

  return (
    <div className="flex">
      <Sidebar user={user} />
      <div className="main-content flex-1">
        <div className="page-header">
          <h1 className="page-title">{t('gear.title', { defaultValue: 'Gear & Kits' })}</h1>
          <p className="page-subtitle">{t('gear.subtitle', { defaultValue: 'Manage photography equipment inventory and who has what' })}</p>
        </div>

        <Tabs defaultValue="kits" className="space-y-6">
          <TabsList className="bg-white/80 backdrop-blur-sm p-1 rounded-lg shadow-sm">
            <TabsTrigger value="kits" data-testid="tab-kits">{t('gear.kits', { defaultValue: 'Kits' })} ({kits.length})</TabsTrigger>
            <TabsTrigger value="items" data-testid="tab-items">{t('gear.items', { defaultValue: 'Items' })} ({items.length})</TabsTrigger>
          </TabsList>

          {/* KITS */}
          <TabsContent value="kits" className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <h2 className="text-xl font-semibold">{t('gear.kitsTitle', { defaultValue: 'Gear Kits' })}</h2>
              {userCanEdit && (
                <Dialog open={openKit} onOpenChange={setOpenKit}>
                  <DialogTrigger asChild>
                    <Button className="btn-gradient" data-testid="create-kit-btn">
                      <Plus className="w-5 h-5 mr-2" />
                      {t('gear.createKit', { defaultValue: 'Create Kit' })}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('gear.createKit', { defaultValue: 'Create Gear Kit' })}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateKit} className="space-y-4">
                      <div className="space-y-2">
                        <Label>{t('gear.kitName', { defaultValue: 'Kit Name' })}</Label>
                        <Input value={kitForm.name} onChange={(e) => setKitForm({ ...kitForm, name: e.target.value })} required placeholder="Telephoto Kit" />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('gear.type', { defaultValue: 'Type' })}</Label>
                        <select
                          value={kitForm.type}
                          onChange={(e) => setKitForm({ ...kitForm, type: e.target.value })}
                          className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg"
                        >
                          <option value="Tele">Tele</option>
                          <option value="Wide">Wide</option>
                          <option value="Drone">Drone</option>
                          <option value="Video">Video</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('gear.mountType', { defaultValue: 'Mount Type' })}</Label>
                        <Input value={kitForm.mount_type} onChange={(e) => setKitForm({ ...kitForm, mount_type: e.target.value })} placeholder="Canon EF" />
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setOpenKit(false)}>{t('common.cancel')}</Button>
                        <Button type="submit" className="btn-gradient">{t('gear.createKit', { defaultValue: 'Create Kit' })}</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            {loading ? (
              <div className="loading-spinner"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div></div>
            ) : kits.length === 0 ? (
              <Card className="glass-card p-12 text-center">
                <Briefcase className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-600">{t('gear.kitsEmpty', { defaultValue: 'No kits created yet' })}</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {kits.map((kit) => (
                  <Card key={kit.id} className="glass-card p-5" data-testid={`kit-card-${kit.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <Briefcase className="w-7 h-7 text-emerald-600" />
                      <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">{kit.type}</span>
                    </div>
                    <h3 className="font-semibold text-base mb-2 truncate">{kit.name}</h3>
                    <div className="text-xs text-slate-600 space-y-0.5 mb-3">
                      <p>{t('gear.mount', { defaultValue: 'Mount' })}: {kit.mount_type || t('gear.universal', { defaultValue: 'Universal' })}</p>
                      <p>{t('gear.itemsCount', { defaultValue: 'Items' })}: {kit.items?.length || 0}</p>
                    </div>
                    <div className="mb-3">{renderOwnerBadge(kit)}</div>
                    {userCanEdit && (
                      <div className="flex gap-2 flex-wrap">
                        {kit.owner_photographer_id ? (
                          <>
                            <Button size="sm" variant="outline" onClick={() => openAssign('kit', kit)}>
                              <UserPlus className="w-3.5 h-3.5 mr-1" /> {t('gear.reassign', { defaultValue: 'Reassign' })}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => unassign('kit', kit.id)} className="text-amber-700">
                              <UserMinus className="w-3.5 h-3.5 mr-1" /> {t('gear.release', { defaultValue: 'Release' })}
                            </Button>
                          </>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => openAssign('kit', kit)}>
                            <UserPlus className="w-3.5 h-3.5 mr-1" /> {t('gear.assign', { defaultValue: 'Assign' })}
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-red-600 ml-auto" onClick={() => deleteGear('kit', kit.id, kit.name)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ITEMS */}
          <TabsContent value="items" className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <h2 className="text-xl font-semibold">{t('gear.itemsTitle', { defaultValue: 'Gear Items' })}</h2>
              {userCanEdit && (
                <Dialog open={openItem} onOpenChange={setOpenItem}>
                  <DialogTrigger asChild>
                    <Button className="btn-gradient" data-testid="add-item-btn">
                      <Plus className="w-5 h-5 mr-2" />
                      {t('gear.addItem', { defaultValue: 'Add Item' })}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('gear.addItem', { defaultValue: 'Add Gear Item' })}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateItem} className="space-y-4">
                      <div className="space-y-2">
                        <Label>{t('gear.itemName', { defaultValue: 'Item Name' })}</Label>
                        <Input value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} required placeholder="Canon 70-200mm f/2.8" />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('gear.type', { defaultValue: 'Type' })}</Label>
                        <select
                          value={itemForm.type}
                          onChange={(e) => setItemForm({ ...itemForm, type: e.target.value })}
                          className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg"
                        >
                          <option value="lens">Lens</option>
                          <option value="body">Camera Body</option>
                          <option value="accessory">Accessory</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('gear.mount', { defaultValue: 'Mount' })}</Label>
                        <Input value={itemForm.mount} onChange={(e) => setItemForm({ ...itemForm, mount: e.target.value })} placeholder="Canon EF" />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('gear.focalRange', { defaultValue: 'Focal Range' })}</Label>
                        <Input value={itemForm.focal_range} onChange={(e) => setItemForm({ ...itemForm, focal_range: e.target.value })} placeholder="70-200mm" />
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setOpenItem(false)}>{t('common.cancel')}</Button>
                        <Button type="submit" className="btn-gradient">{t('gear.addItem', { defaultValue: 'Add Item' })}</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            {loading ? (
              <div className="loading-spinner"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div></div>
            ) : items.length === 0 ? (
              <Card className="glass-card p-12 text-center">
                <Camera className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-600">{t('gear.itemsEmpty', { defaultValue: 'No items added yet' })}</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((item) => (
                  <Card key={item.id} className="glass-card p-5" data-testid={`item-card-${item.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <Camera className="w-7 h-7 text-blue-600" />
                      <span className="px-2.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium capitalize">{item.type}</span>
                    </div>
                    <h3 className="font-semibold text-base mb-2 truncate">{item.name}</h3>
                    <div className="text-xs text-slate-600 space-y-0.5 mb-3">
                      {item.mount && <p>{t('gear.mount', { defaultValue: 'Mount' })}: {item.mount}</p>}
                      {item.focal_range && <p>{t('gear.focal', { defaultValue: 'Focal' })}: {item.focal_range}</p>}
                    </div>
                    <div className="mb-3">{renderOwnerBadge(item)}</div>
                    {userCanEdit && (
                      <div className="flex gap-2 flex-wrap">
                        {item.owner_photographer_id ? (
                          <>
                            <Button size="sm" variant="outline" onClick={() => openAssign('item', item)}>
                              <UserPlus className="w-3.5 h-3.5 mr-1" /> {t('gear.reassign', { defaultValue: 'Reassign' })}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => unassign('item', item.id)} className="text-amber-700">
                              <UserMinus className="w-3.5 h-3.5 mr-1" /> {t('gear.release', { defaultValue: 'Release' })}
                            </Button>
                          </>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => openAssign('item', item)}>
                            <UserPlus className="w-3.5 h-3.5 mr-1" /> {t('gear.assign', { defaultValue: 'Assign' })}
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-red-600 ml-auto" onClick={() => deleteGear('item', item.id, item.name)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Assign dialog */}
        <Dialog open={!!assignTarget} onOpenChange={(v) => !v && setAssignTarget(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {assignTarget?.current
                  ? t('gear.reassignTitle', { defaultValue: 'Reassign {{name}}', name: assignTarget?.name })
                  : t('gear.assignTitle', { defaultValue: 'Assign {{name}}', name: assignTarget?.name })}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Label>{t('gear.photographer', { defaultValue: 'Photographer' })}</Label>
              <Select value={assignTo} onValueChange={setAssignTo}>
                <SelectTrigger><SelectValue placeholder={t('gear.pickPhotographer', { defaultValue: 'Pick a photographer' })} /></SelectTrigger>
                <SelectContent>
                  {photographers.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">{t('gear.singleOwnerHint', { defaultValue: 'Each kit/item can be held by only one photographer at a time. The current holder will be replaced.' })}</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssignTarget(null)}>{t('common.cancel')}</Button>
              <Button onClick={submitAssign}>{t('gear.confirmAssign', { defaultValue: 'Confirm' })}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
